import {
  buildExternalCalendarFeedBreadcrumb,
  fromZonedTime,
  toZonedEventTimes,
} from '@griply/utils';
import { getIdToken } from 'firebase/auth';
import { FunctionsError, httpsCallable } from 'firebase/functions';
import { getErrorReporter } from 'shared/services/error-tracking';
import { Timestamp } from 'shared/types/timestamp';

import { getAuthentication, getFunctions } from './helpers';
import { externalCalendarSanitizer } from './helpers/external-calendar-sanitizer';

// The Functions SDK sends a call without a token when fetching one fails, and the backend answers
// unauthenticated; forcing a refresh lets the caller's next attempt carry a valid one.
const refreshIdTokenIfUnauthenticated = async (
  error: unknown,
): Promise<never> => {
  const user = (await getAuthentication()).currentUser;
  if ((error as FunctionsError).code === 'functions/unauthenticated' && user) {
    await getIdToken(user, true).catch(() => undefined);
  }
  throw error;
};

export type Options = {
  startDate: Timestamp;
  endDate: Timestamp;
  timezone: string;
};

export const getExternalCalendarEvents = async ({
  startDate,
  endDate,
  timezone,
}: Options) => {
  const functions = getFunctions();
  // Provider-authored data: only the sanitizer turns it into ExternalCalendarEvent.
  const fetchEvents = httpsCallable<
    { startDate: string; endDate: string; timezone: string },
    unknown
  >(functions, 'externalCalendars-getEvents');

  // The window arrives as the wall clock the grid shows — `timezone`'s — not as real instants.
  const { data } = await fetchEvents({
    startDate: fromZonedTime(startDate, timezone).toISOString(),
    endDate: fromZonedTime(endDate, timezone).toISOString(),
    timezone,
  }).catch(refreshIdTokenIfUnauthenticated);

  const events = externalCalendarSanitizer.sanitizeEvents(data);
  getErrorReporter().addBreadcrumb(
    buildExternalCalendarFeedBreadcrumb(
      events.length,
      Array.isArray(data) ? data.length : 0,
    ),
  );

  return events.map((event) => toZonedEventTimes(event, timezone));
};
